<template>
  <div>
    <div v-for="(accred, index) of accreditations" :key="index" class="row mb-3">
      <div class="col">
        <b-card :body-class="'p-3 shadow rounded' + ((activeCard === index) ? ' visible-lead-types' : '')">
          <div class="row align-items-center accreditation fadein-details">
            <div class="col accreditation-code">
              <b-form-group :label="accred.question + ((accred.compulsory === true) ? ' *' : '')" :label-class="'font-weight-bold'">
                <b-form-select
                  v-if="accred.options.length > 1"
                  v-model="accred.selectedOption"
                  size="sm"
                  :options="accred.options"
                  @input="onChange"
                  @change="setActiveState(index)"
                />
                <b-form-input
                  v-if="accred.storeCode"
                  v-model="accred.value"
                  :class="'mt-2'"
                  size="sm"
                  :placeholder="$t('Enter your accreditation number or code')"
                  trim
                  @focus="setActiveState(index)"
                  @input="onChange"
                />
                <b-form-select
                  v-else-if="accred.storeDate"
                  v-model="accred.value"
                  size="sm"
                  @change="setActiveState(index)"
                  @input="onChange"
                >
                  <option value="" disabled selected>
                    {{ $t('Please choose a value') }}
                  </option>
                  <option value="1">
                    {{ $t('Yes') }}
                  </option>
                  <option value="0">
                    {{ $t('No') }}
                  </option>
                </b-form-select>
              </b-form-group>
            </div>
            <div class="col accreditation-status">
              <p
                v-b-tooltip.hover
                class="font-weight-500 mb-2 approval-status"
                :title="isApproved(accred.approved_until) ? '' : accreditationStatusTooltip.title"
              >
                {{ $t('Approval Status') }}:
                <span
                  class="ml-2 accreditation-status-text"
                  :class="isApproved(accred.approved_until) ? 'approved' : 'pending'"
                >
                  {{ isApproved(accred.approved_until) ? $t('Approved') : $t('Pending') }}
                  <font-awesome-icon
                    :class="'ml-2'"
                    :icon="isApproved(accred.approved_until) ? 'check-circle' : 'question-circle'"
                  />
                </span>
              </p>
              <p v-if="isApproved(accred.approved_until)" class="font-weight-light small mb-0">
                {{ $t('All lead types can be used') }}.
                <span v-if="showLeadTypesButton">{{ $t('Click Show lead types to see all available Lead types') }}.</span>
              </p>
              <div v-else class="font-weight-light">
                <p class="mb-0 small">
                  {{ $t('This accreditation is pending approval') }}. <br>
                  <span v-html="$t('leads.approved')" />
                </p>
              </div>
            </div>
            <div v-if="showLeadTypesButton" class="col col-md-3 accreditation-lead-type">
              <b-button
                size="sm"
                variant="outline-secondary"
                :disabled="!isApproved(accred.approved_until)"
                @click="showLeadTypes(index, accred)"
              >
                <span
                  v-b-tooltip.hover
                  class="d-flex justify-content-center w-100 stretched-link"
                  :title="!isApproved(accred.approved_until) ? $t('Your accreditation is pending'): ''"
                >{{ $t('Show Lead Types') }}</span>
              </b-button>
            </div>
          </div>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import dates from "@/services/date";

export default {
  name: 'AccreditationsList',
  props:['showLeadTypesButton', 'coverage'],
  data() {
    return {
      accreditations: {},
      originalAccreditations: {},
      busy: true,
      activeCard: null,
      accreditationStatusTooltip: {
        title: this.$t('pendingToolTip')
      }
    }
  },
  computed: {
    empty: function() {
      return Object.values(this.accreditations).find(a => a.value !== '') === undefined;
    }
  },
  methods: {
    isApproved(approved_until) {
      if (approved_until !== null) {
        return dates.futureDate(approved_until);
      }
      return false;
    },
    async onSubmit() {
      const accreds = Object.entries(this.accreditations).map(([, accred]) => ({
        id: accred.selectedOption,
        certificate_number: accred.value,
      }));

      let result = false;
      try {
        await this.$api.company.saveAccreditations(accreds);

        for (const accredCode in this.accreditations) {
          if (this.accreditations[accredCode].value !== this.originalAccreditations[accredCode].value) {
            this.accreditations[accredCode].approved_until = null;
          }
        }

        this.originalAccreditations = JSON.parse(JSON.stringify(this.accreditations));
        result = true;
      } catch (e) {
        result = false;
      }

      return result;
    },
    onChange() {
      this.$emit('change');
    },
    showLeadTypes: function (id, accred) {
      this.activeCard = id;
      this.$emit('selected', accred);
    },
    setActiveState: function(index) {
      this.activeCard = index;
    },
  },
  created() {
    let coverage = null;
    if (this.coverage) {
      coverage = this.coverage.id;
    }
    this.$api.company.accreditations(coverage).then((result) => {
      const {compulsoryAccreditations, optionalAccreditations, companyAccreditations} = result.data.data;
      const groupedAccreditations = {};

      const accreditations = compulsoryAccreditations.map((accred) => ({...accred, compulsory: true}))
        .concat(optionalAccreditations.map((accred) => ({...accred, compulsory: false})));

      for (const accred of accreditations) {
        const companyAccred = companyAccreditations.filter((a) => a.accreditation.code === accred.code);
        if (groupedAccreditations[accred.code] === undefined) {
          groupedAccreditations[accred.code] = {
            name: accred.name,
            question: accred.question,
            storeCode: accred.store_code,
            storeDate: accred.store_date,
            compulsory: accred.compulsory,
            selectedOption: (companyAccred.length > 0 ? companyAccred[0].accreditation.id : accred.id),
            companyAccreditation: companyAccred[0],
            value: companyAccred.length > 0 ? companyAccred[0].certificate_number : '',
            options: [],
            approved_until: companyAccred.length > 0 ? companyAccred[0].manual_approval_until : null,
          };
        }
        groupedAccreditations[accred.code].options.push({
          value: accred.id,
          text: accred.accreditor_description,
        });
        if (groupedAccreditations[accred.code].options.length > 1 && companyAccred.length === 0) {
          groupedAccreditations[accred.code].selectedOption = '';
        }
      }

      for (const accred in groupedAccreditations) {
        if (groupedAccreditations[accred].options.length > 1) {
          groupedAccreditations[accred].options.unshift({
            value: '',
            text:  this.$t('Please choose a value'),
            disabled: true
          });
        }
      }

      this.accreditations = groupedAccreditations;
      this.originalAccreditations = JSON.parse(JSON.stringify(groupedAccreditations));
      this.busy = false;
      if (Object.keys(this.accreditations).length <1) {
        this.$emit('empty');
      }
      this.$emit('loaded');
    });
  }
}
</script>

<style scoped lang="scss">
.card-body {
  &.visible-lead-types {
    @include font-size($font-size-14);
    transition: $transition-base;

    &:before {
      background: $theme-primary-accents-900;
      content: '\200C';
      left: 0;
      position: absolute;
      top: 0;
      transition: $transition-base;

      @include media-breakpoint-up(md) {
        width: 8px;
        height: 100%;
      }
      @include media-breakpoint-down(sm) {
        width: 100%;
        height: 8px;
      }
    }
  }
}

.approval-status {
  @include font-size($font-size-14);
}

.accreditation-status-text {
  &.approved {
    color: $theme-secondary;
  }
  &.pending {
    color: $theme-tertiary-accents-900;
  }
}

@media only screen and (max-width: 690px) {
  .accreditation-code {
    order: 0;
    flex: 0 0 100%;
    max-width: 100%;
  }

  .accreditation-status {
    order: 1;
    flex: 0 0 100%;
    max-width: 100%;
  }

  .accreditation-lead-type {
    order: 2;
    flex: 0 0 100%;
    max-width: 100%;
    text-align: center;
    margin-top: 1rem;
  }
}
</style>
